/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { getSettingsNav, type SettingsNavOptions, type SettingsSection } from '../constants/settings-nav';

interface SettingsTabsProps {
  active: SettingsSection;
  onNavigate: (section: SettingsSection) => void;
  /** The same gating options the rest of the surface uses. */
  navOptions: SettingsNavOptions;
  /** Sections carrying an unresolved pendency, which get the amber dot. */
  pendingSections: Set<SettingsSection>;
  ariaLabel: string;
}

/**
 * The tab strip is evenly spaced: no group gaps.
 *
 * The groups used to be drawn as a wider gap between the last item of one and
 * the first of the next. It read as broken alignment rather than as grouping —
 * one four-pixel rhythm interrupted three times, with `Assinatura` stranded
 * alone between two wide gaps. The grouping still exists in `settings-nav`
 * and still orders the tabs; it just no longer tries to show itself through
 * whitespace.
 */
const TAB_GAP = 'var(--space-2)';

/**
 * Renders the amber dot that marks a tab holding an unresolved pendency.
 *
 * @returns The dot element.
 */
function PendingDot() {
  return (
    <Text
      component="span"
      aria-hidden="true"
      style={{
        width: 6, height: 6, borderRadius: '50%',
        background: 'var(--accent-mostarda)', display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

/**
 * The desktop section control: eight evenly spaced tabs across the top.
 *
 * Replaces the 240px sidebar, which spent a quarter of the page's width
 * listing eight words and left the profile's two-column field grid squeezed
 * into what was left. `SettingsSectionPicker` remains the narrow-screen form
 * of this same control.
 *
 * @param props - Component props.
 * @returns The tab strip.
 */
export function SettingsTabs({
  active, onNavigate, navOptions, pendingSections, ariaLabel,
}: SettingsTabsProps) {
  const { t } = useTranslation('features/agency-settings');
  const items = getSettingsNav(t, navOptions);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid="settings-tabs"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: TAB_GAP,
        // Wraps rather than scrolls: a hidden scroll region loses the tabs
        // that fall off the end, and eight short words wrap to two rows
        // without costing anything.
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {items.map((item) => {
        const isActive = item.key === active;
        return (
          <UnstyledButton
            key={item.key}
            role="tab"
            aria-selected={isActive}
            data-testid={`settings-tab-${item.key}`}
            onClick={() => onNavigate(item.key)}
            style={{
              position: 'relative',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              height: 40,
              padding: '0 var(--space-3)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: isActive ? 700 : 500,
              color: isActive ? 'var(--seed-base)' : 'var(--text-secondary)',
              // An inset shadow rather than a border: a border changes the
              // box height and nudges every other tab by a pixel as the
              // selection moves.
              boxShadow: isActive ? 'inset 0 -2px 0 var(--seed-base)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
            {/* The active tab is already open, so its dot would be telling the
                user about a screen they are looking at. */}
            {!isActive && pendingSections.has(item.key) && <PendingDot />}
          </UnstyledButton>
        );
      })}
    </div>
  );
}
